import type { Day, Duration } from 'date-fns';
import {
  add,
  differenceInDays,
  differenceInYears,
  format,
  getDay,
  isBefore,
  isPast,
  parse,
  setDay,
  startOfDay
} from 'date-fns';

type DaysOfWeek = {
  Sunday: Day;
  Monday: Day;
  Tuesday: Day;
  Wednesday: Day;
  Thursday: Day;
  Friday: Day;
  Saturday: Day;
};

const dayToInteger: DaysOfWeek = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6
};

export const parseDate = (date: string | Date, formatString = 'yyyy-MM-dd') => {
  if (typeof date === 'string') {
    return parse(date, formatString, new Date());
  }

  return date;
};

/**
 * Parses a yyyy-MM-dd calendar date as UTC midnight. Pair with `timeZone: 'UTC'` when formatting with
 * next-intl, whose default time zone is the server's (UTC) rather than the browser's.
 */
export const parseDateAsUtc = (date: string) => {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

export const shortDayShortMonth = (date: Date | string) => {
  return format(parseDate(date), 'EEE dd MMM yy'); // eg Wed 02 Mar 22
};

export const shortDay = (date: Date | string) => {
  return format(parseDate(date), 'EEE'); // eg Wed
};

export const fullDate = (date: Date | string) => {
  return format(parseDate(date), 'EEEE d MMMM yyyy'); // eg Wednesday, 2 March 2022
};

export const shortDateMonth = (date: Date | string) => {
  return format(parseDate(date), 'dd MMM y'); // eg 02 Mar 2022
};

export const shortDateMonthYear = (date: Date | string) => {
  return format(parseDate(date), 'dd MMM yy'); // eg 02 Mar 22
};

export const formatDoShortMonth = (date: Date | string) => {
  return format(parseDate(date), 'do MMM'); // eg 2nd Mar
};

export const formatDoShortMonthYear = (date: Date | string) => {
  return format(parseDate(date), 'do MMM y'); // eg 2nd Mar 2022
};

export const formatDayShortDate = (date: Date | string) => {
  return format(parseDate(date), 'EEE, d MMM'); // eg Wed, 7 May
};

export const formatDayShortDateYear = (date: Date | string) => {
  return format(parseDate(date), 'EEE, d MMM, yy'); // eg Thu, 8 May, 26
};

export const formatDayFullDate = (date: Date | string) => {
  return format(parseDate(date), 'EEE, d MMMM'); // eg Wed, 7 March
};

export const formatDayFullDateYear = (date: Date | string) => {
  return format(parseDate(date), 'EEE, d MMMM yyyy'); // eg Wed, 7 March 2026
};

export const formatSlashedDDMMYYYY = (date: Date | string) => {
  return format(parseDate(date), 'dd/MM/yyyy'); // eg 02/03/2022
};
export const formatSlashedDDMMYY = (date: Date | string) => {
  return format(parseDate(date), 'dd/MM/yyyy'); // eg 02/03/22
};

export const formatSlashedDDMM = (date: Date | string) => {
  return format(parseDate(date), 'dd/MM'); // eg 02/03
};

export const formatDashedYYYYMMDD = (date: Date | string) => {
  return format(parseDate(date), 'yyyy-MM-dd'); // eg 2022-03-02
};

export const formatMonthOfYear = (date: Date | string) => {
  return format(parseDate(date), 'LL'); // 02
};

export const formatMonth = (date: Date | string) => {
  return format(parseDate(date), 'MMMM'); // March
};

export const formatYear = (date: Date | string) => {
  return format(parseDate(date), 'yyyy'); // 2022
};

export const formatMonthYear = (date: Date | string) => {
  return format(parseDate(date), 'MMMM yyyy'); // March 2022
};

export const formatDurationHM = (duration: Duration) => {
  return format(add(startOfDay(new Date(0)), duration), "H'hrs' m'm'"); // eg 6hrs 15m
};

export const getSuggestedTripStartDate = (
  duration: Duration = { weeks: 8 },
  options: { startDayOfWeek?: number } = {}
) => {
  let startDate = add(new Date(), duration);

  if (options?.startDayOfWeek) {
    startDate = setDay(startDate, options.startDayOfWeek);
  }

  return startDate;
};

export const getTripLengthInDays = (startDate: Date | string, endDate: Date | string) => {
  return differenceInDays(parseDate(endDate), parseDate(startDate)) + 1; // include end day
};

export const getDurationHoursMins = (duration: number) => {
  const minutes = duration % 60;
  const hours = Math.floor(duration / 60);
  return `${hours}hrs ${minutes}mins`;
};

type DatePriced = {
  date: string;
  priceTier: string;
};

export const isUnavailableDate = (
  date: Date,
  unavailableDaysOfWeek: string[] = [],
  futurePricingAvailability: DatePriced[] = [],
  unavailableDates: string[] = []
) => {
  const pricingTier =
    futurePricingAvailability?.length && date
      ? futurePricingAvailability.find((futureDate) => futureDate.date === formatDashedYYYYMMDD(date))?.priceTier
      : null;
  const unavailableDays = unavailableDaysOfWeek?.map((day) => dayToInteger[day as keyof DaysOfWeek]);
  const dayOfWeek = getDay(date) as Day;
  const isUnavailableDate = unavailableDates && date ? unavailableDates?.includes(formatDashedYYYYMMDD(date)) : null;
  const minBookingDate = getSuggestedTripStartDate({ weeks: 2 });
  return (
    unavailableDays?.includes(dayOfWeek) ||
    isBefore(date, minBookingDate) ||
    isPast(date) ||
    pricingTier === 'unavailable' ||
    isUnavailableDate
  );
};

export function getAgeOnDate({ birthDate, endDate }: { birthDate: string; endDate: string }): number {
  return differenceInYears(parseDate(endDate), parseDate(birthDate));
}
